import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  catchError,
  finalize,
  mergeMap,
  Observable,
  take,
  throwError,
} from "rxjs";
import { TokenService } from "./token.service";
import { StorageService } from "./storage.service";
import { DEFAULT_ERROR_MESSAGE } from "../shared/constants/misc";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { ConfirmationDialogComponent } from "../dashboard/orders/modals/confirmation-dialog/confirmation-dialog.component";
import { INVOICE_HEADER_BLACKLIST_ENDPOINTS } from "../shared/constants/invoice-header-blacklist-endpoints";
import { environment } from "src/environments/environment";
import { UserService } from "../dashboard/user/services/user.service";
import { SharedService } from "./shared.service";

@Injectable({
  providedIn: "root",
})
export class InterceptorService {
  modalRef!: BsModalRef;

  constructor(
    private router: Router,
    private tokenService: TokenService,
    private storageService: StorageService,
    private modalService: BsModalService,
    private userService: UserService,
    private sharedService: SharedService,
  ) {}

  invalidTokenError(error: HttpErrorResponse): boolean {
    let errorsArray = error.error.errors;
    if (errorsArray === undefined) return false;
    if (
      errorsArray.find((object: any) => object.type === "InvalidTokenError")
    ) {
      return true;
    }
    return false;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    /**
     * Convert emails to lowercase
     */

    if (request.headers.has("InterceptorSkipHeader")) {
      const headers = request.headers.delete("InterceptorSkipHeader");
      return next
        .handle(request.clone({ headers }))
        .pipe(catchError(this.handleAuthError));
    }

    if (!this.tokenService.hasToken()) {
      return next.handle(request).pipe(catchError(this.handleAuthError));
    }
    const startSession =
      sessionStorage.getItem("startSession") === "true" ? "true" : "";
    const uid = localStorage.getItem("userEmail") || "";
    const isBaseApi = request.url.startsWith(environment.apiBaseUrl);
    const isAptCheck =
      !isBaseApi && request.url.includes(environment.aptCheckBaseUrl);
    const isAsmApi = request.url.startsWith(
      `${environment.apiBaseUrl}/`
    );
    const setHeaders: Record<string, string> = isAsmApi
      ? {
          BearerToken: "Bearer " + this.tokenService.getToken(),
        }
      : isAptCheck
      ? {
          BearerToken: "Bearer " + this.tokenService.getToken(),
          impersonateFlag: startSession,
          customerId: uid,
        }
      : {
          BearerToken: "Bearer " + this.tokenService.getToken(),
          // Authorization:
          //   "Basic " +
          //   btoa(`${environment.sentinentUser}:${environment.sentinentPwd}`),
          impersonateFlag: startSession,
          customerId: uid.toLowerCase(),
        };

    return next
      .handle(request.clone({ setHeaders }))
      .pipe(catchError(this.handleAuthError));
  }

  openConfirmationModal(data: any = {}) {
    const initialState: ModalOptions = {
      backdrop: true,
      ignoreBackdropClick: true,
      initialState: {
        ...data,
      },
    };
    this.modalRef = this.modalService.show(
      ConfirmationDialogComponent,
      Object.assign(initialState, {
        id: data?.modalId || "",
        class: "modal-md modal-dialog-centered",
      }),
    );
  }

  handleError(error: any, config: any) {
    const errorCode = error?.error?.errors?.[0]?.subject;
    if (errorCode === "0002") {
      const errorMessage = `You have logged in to more than one browser with different ${
        config?.isCSR ? "retailers" : "accounts"
      }. Please select the account again`;
      if (config?.isCSR || config?.isSalesOpsUser || config?.isSalesPerson) {
        this.userService
          .setDefaultUnit({
            username: config?.uid,
          })
          .pipe(
            take(1),
            finalize(() => {
              this.storageService.removeItem("cart");
              this.storageService.removeItem("selected-builder");
              this.storageService.removeItem("salesman-selected");
              this.userService
                .getCustomerDetails({
                  username: config?.uid,
                  addInIndexDB: true,
                })
                .pipe(
                  take(1),
                  finalize(() => {
                    setTimeout(() => {
                      this.sharedService.setSyncWithIndexDB(Date.now());
                      if (config?.isCSR) {
                        this.router.navigate(["/csr"], {
                          queryParams: { exit: true },
                        });
                      } else {
                        this.router.navigate(["/salesman-view-account"]);
                      }
                      this.openConfirmationModal({
                        title: "Error",
                        content: errorMessage,
                        primaryActionLabel: "Ok",
                        disableSecondaryAction: true,
                        onPrimaryAction: () => {
                          window.location.reload();
                        },
                      });
                    }, 1000);
                  }),
                )
                .subscribe();
            }),
          )
          .subscribe();
      }
    }
  }

  getReqHeaders(config: any, request: any) {
    const endpoint = request.urlWithParams;
    const invoiceAccountUid = config?.orgUnit?.uid;
    const isEndpointPatternBlacklisted =
      INVOICE_HEADER_BLACKLIST_ENDPOINTS.some((item) => {
        // @ts-ignore
        const pattern = new URLPattern(item, environment.apiBaseUrl);
        return pattern.test(endpoint);
      });
    const startSession = sessionStorage.getItem("startSession");
    let uid = localStorage.getItem("userEmail") || "";
    const reqHeaders: any = {
      Authorization: "Bearer " + this.tokenService.getToken(),
      impersonateFlag: startSession === "true" ? startSession : "",
      customerId: startSession === "true" ? uid : "",
    };
    if (
      invoiceAccountUid &&
      invoiceAccountUid !== "EMPTY_B2BUNIT" &&
      !isEndpointPatternBlacklisted
    ) {
      reqHeaders["invoiceAccount"] = invoiceAccountUid || "";
    }
    return reqHeaders;
  }

  currentConfig$() {
    return this.storageService.getItem("customer-details").pipe(take(1));
  }

  convertAllEmailsToLowerCase(url: string): string {
    const emailRegex = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
    return url.replace(emailRegex, (email) => email.toLowerCase());
  }
  private redirectingToLogin = false;
  private handleAuthError = (error: any): Observable<never> => {
    if (
      !this.redirectingToLogin &&
      (this.invalidTokenError(error) || this.unAuthorizedError(error))
    ) {
      this.redirectingToLogin = true;
      // this.sessionService.redirectToLogin(this.router.url);
    }
    return throwError(() => error);
  };

  unAuthorizedError(error: HttpErrorResponse): boolean {
    return this.hasErrorType(error, "UnauthorizedError");
  }

  private hasErrorType(error: HttpErrorResponse, type: string): boolean {
    const errorsArray = error?.error?.errors;
    if (!Array.isArray(errorsArray)) return false;
    return errorsArray.some((o: any) => o?.type === type);
  }
}
